import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { MAX_PRICE, MIN_PRICE, POWER_TYPE_FILTERS } from '@/components/vehicle-search-filters/constants';
import { logTrackingEvent } from '@/utils/tracking';
import { useRouter } from 'next/router';

import BodyStyleToggles from './body-style-toggles';
import { LifestylePreferenceToggles } from './lifestyle-preference-toggles';
import { PowerTypeToggles } from './power-type-toggles';
import { PriceRangeSlider } from './price-range-slider';

const BROWSE_SAFE_OPTIONS_EVENT_TEXT = 'Browse safe options';

const shouldOmitMaxPriceWhenReachingMax = (key: string, value: number | boolean | string[]) => {
  if (key === 'maxPrice' && value === MAX_PRICE) {
    return false;
  }
  return true;
};

const DiscoveryQuestionsInput = () => {
  const [minPrice, setMinPrice] = useState<number>(MIN_PRICE);
  const [maxPrice, setMaxPrice] = useState<number>(MAX_PRICE);
  const [bodyTypes, setBodyTypes] = useState<string[]>([]);
  const [pPlaterApproved, setPPlaterApproved] = useState<boolean>(false);
  const [safeCarsForFamilies, setSafeCarsForFamilies] = useState<boolean>(false);
  const [powerTypes, setPowerTypes] = useState<string[]>([POWER_TYPE_FILTERS.ANY.value]);

  const router = useRouter();

  const handleSubmit = () => {
    const entries = {
      minPrice,
      maxPrice,
      bodyTypes,
      pPlaterApproved,
      safeCarsForFamilies,
      powerTypes,
    };

    const entriesToParam = Object.entries(entries).filter(([key, value]) => {
      return shouldOmitMaxPriceWhenReachingMax(key, value);
    });

    const searchResultsQueryParams = entriesToParam.reduce((acc, [key, value]) => {
      const sholdBeOmitted =
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        (Array.isArray(value) && value.length == 1 && value[0] == 'Any');
      if (sholdBeOmitted) {
        return acc;
      }
      return { ...acc, [key]: value.toString() };
    }, {});

    router.push(`/results?${new URLSearchParams(searchResultsQueryParams).toString()}`);
  };

  const handleClick = () => {
    logTrackingEvent({
      event: 'interaction_click',
      component_name: 'button',
      click_text: BROWSE_SAFE_OPTIONS_EVENT_TEXT,
      click_url: '',
    });
    handleSubmit();
  };

  return (
    <form
      aria-labelledby="discovery-questions-input-title"
      className="mx-auto w-full min-w-[310px] max-w-[420px] md:w-[660px] md:max-w-none flex flex-col items-center gap-8 rounded-lg border-2 border-greyscale-200 bg-greyscale-0 py-6 px-4 md:p-12"
      onSubmit={(e) => {
        e.preventDefault();
        handleClick();
      }}
    >
      <h2 id="discovery-questions-input-title" className="text-center font-black text-2xl md:text-3xl">
        Browse safe cars on any budget
      </h2>
      <PriceRangeSlider minPrice={minPrice} maxPrice={maxPrice} setMinPrice={setMinPrice} setMaxPrice={setMaxPrice} />
      <BodyStyleToggles value={bodyTypes} onChange={setBodyTypes} />
      <LifestylePreferenceToggles
        pPlaterApproved={pPlaterApproved}
        setPPlaterApproved={setPPlaterApproved}
        safeCarsForFamilies={safeCarsForFamilies}
        setSafeCarsForFamilies={setSafeCarsForFamilies}
      />
      <PowerTypeToggles powerTypes={powerTypes} setPowerTypes={setPowerTypes} />
      <Button className="w-full" type="submit" variant="primary">
        {BROWSE_SAFE_OPTIONS_EVENT_TEXT}
      </Button>
    </form>
  );
};

export default DiscoveryQuestionsInput;
